import React, { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, money } from "@/lib/api";
import { PageHead } from "./Layout";

const F = ({ label, hint, children }: any) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

function Preview({ sid, version, unit }: { sid: string; version: number; unit: string }) {
  const [sq, setSq] = useState("1000");
  const [r, setR] = useState<any>(null);
  useEffect(() => {
    const n = Number(sq);
    if (!n || n <= 0) { setR(null); return; }
    const t = setTimeout(() => api.post(`/orgs/me/services/${sid}/preview`, { sq_ft: n }).then((x) => setR(x.data)).catch(() => setR(null)), 300);
    return () => clearTimeout(t);
  }, [sq, sid, version]);
  return (
    <div className="bg-secondary/60 rounded-lg p-4" data-testid={`service-preview-${sid}`}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Test quote (saved prices)</p>
      <div className="flex items-center gap-2">
        <Input className="h-10 w-28 font-mono" value={sq} onChange={(e) => setSq(e.target.value)} inputMode="decimal" data-testid={`preview-sqft-${sid}`} />
        <span className="text-sm text-muted-foreground">{unit} →</span>
      </div>
      {r && (
        <div className="mt-3">
          <p className="font-heading text-2xl font-bold" data-testid={`preview-result-${sid}`}>
            {r.low_cents === r.high_cents ? money(r.estimate_cents) : `${money(r.low_cents)} – ${money(r.high_cents)}`}
          </p>
          <p className="text-xs text-muted-foreground">Central estimate {money(r.estimate_cents)}{r.minimum_applied ? " · minimum applied" : ""}</p>
        </div>
      )}
    </div>
  );
}

function ServiceCard({ s, onChanged }: { s: any; onChanged: () => void }) {
  const [f, setF] = useState<any>(s);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);
  useEffect(() => setF(s), [s]);
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async (patch: any = {}) => {
    setBusy(true);
    try {
      const body = { name: f.name, measurement_type: f.measurement_type, rate_per_sq_ft: f.rate_per_sq_ft, base_fee: f.base_fee, minimum_job: f.minimum_job, range_pct: Number(f.range_pct),
        max_area_sq_ft: Number(f.max_area_sq_ft), area_precision: Number(f.area_precision), note: f.note, ...patch };
      await api.patch(`/orgs/me/services/${s.id}`, body);
      toast.success("Service saved");
      setVersion((v) => v + 1);
      onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const archive = async () => { if (window.confirm(`Archive "${s.name}"?`)) { await api.delete(`/orgs/me/services/${s.id}`); onChanged(); } };
  const tid = s.id.slice(0, 8);
  const lin = f.measurement_type === "length";
  const u = lin ? "linear ft" : "sq ft";
  return (
    <div className="bg-card border rounded-xl p-6" data-testid={`service-card-${tid}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-mono uppercase tracking-wider bg-accent text-accent-foreground rounded px-2 py-1">{s.measurement_type === "length" ? "Length" : "Area"} · per {s.pricing_unit === "linear_ft" ? "linear ft" : "sq ft"}</span>
          <span className={`text-xs ${s.active ? "text-primary" : "text-muted-foreground"}`}>{s.active ? "Published" : "Draft"}</span>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">Published
            <Switch checked={s.active} onCheckedChange={(v) => save({ active: v })} disabled={busy} data-testid={`service-active-switch-${tid}`} />
          </label>
          <Button variant="ghost" size="icon" onClick={archive} aria-label="Archive service" data-testid={`service-archive-${tid}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="grid lg:grid-cols-[1fr_260px] gap-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="sm:col-span-2 lg:col-span-3"><F label="Service name"><Input value={f.name} onChange={set("name")} maxLength={80} data-testid={`service-name-${tid}`} /></F></div>
          <div className="sm:col-span-2"><F label="Measurement type" hint={f.measurement_type !== s.measurement_type ? "Existing leads keep their original units." : undefined}>
            <MeasurementSelect value={f.measurement_type} onChange={set("measurement_type")} testid={`service-measurement-${tid}`} />
          </F></div>
          <F label={`$ per ${u}`}><Input value={f.rate_per_sq_ft} onChange={set("rate_per_sq_ft")} inputMode="decimal" className="font-mono" data-testid={`service-rate-${tid}`} /></F>
          <F label="Minimum job charge ($)"><Input value={f.minimum_job} onChange={set("minimum_job")} inputMode="decimal" className="font-mono" data-testid={`service-minimum-${tid}`} /></F>
          <F label="Base / setup fee ($)"><Input value={f.base_fee} onChange={set("base_fee")} inputMode="decimal" className="font-mono" data-testid={`service-base-${tid}`} /></F>
          <F label="Estimate range ± %" hint="0–25. 0 shows one price."><Input type="number" min={0} max={25} value={f.range_pct} onChange={set("range_pct")} data-testid={`service-range-${tid}`} /></F>
          <F label={`Max online ${lin ? "length (ft)" : "area (sq ft)"}`} hint="Larger jobs go to manual review."><Input type="number" value={f.max_area_sq_ft} onChange={set("max_area_sq_ft")} data-testid={`service-maxarea-${tid}`} /></F>
          <F label={lin ? "Length decimals" : "Area decimals"}><Input type="number" min={0} max={2} value={f.area_precision} onChange={set("area_precision")} data-testid={`service-precision-${tid}`} /></F>
          <div className="sm:col-span-2 lg:col-span-3"><F label="Note shown to homeowners (optional)"><Textarea rows={2} value={f.note} onChange={set("note")} data-testid={`service-note-${tid}`} /></F></div>
          <div><Button onClick={() => save()} disabled={busy} data-testid={`service-save-${tid}`}>{busy ? "Saving…" : "Save prices"}</Button></div>
        </div>
        <Preview sid={s.id} version={version} unit={s.pricing_unit === "linear_ft" ? "linear ft" : "sq ft"} />
      </div>
    </div>
  );
}

const MeasurementSelect = ({ value, onChange, testid }: { value: string; onChange: (v: string) => void; testid: string }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="area" data-testid={`${testid}-area`}>Area — Square Feet (sq ft)</SelectItem>
      <SelectItem value="length" data-testid={`${testid}-length`}>Length — Linear Feet (linear ft)</SelectItem>
    </SelectContent>
  </Select>
);

function AddService({ onAdded }: { onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", measurement_type: "area", rate_per_sq_ft: "0" });
  const add = async () => {
    try { await api.post("/orgs/me/services", f); setOpen(false); onAdded(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="add-service-button"><Plus className="h-4 w-4 mr-1" />Add service</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Add a service</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <F label="Service name" hint="Any name, e.g. Mosquito & Tick Treatment or Gutter Cleaning."><Input value={f.name} maxLength={80} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-service-name" /></F>
          <F label="Measurement type" hint="Area uses the outline tool; Length uses the line tool.">
            <MeasurementSelect value={f.measurement_type} onChange={(v) => setF({ ...f, measurement_type: v })} testid="new-service-measurement" />
          </F>
          <Button onClick={add} data-testid="new-service-submit">Create as draft</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Services() {
  const [list, setList] = useState<any[] | null>(null);
  const load = () => api.get("/orgs/me/services").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="fade-up">
      <PageHead title="Services & Pricing" sub="Name each service however you like. Area services are priced per sq ft and measured with the outline tool; Length services are priced per linear ft and measured with the line tool. Prices are applied on our server to the homeowner’s validated measurement; homeowners never see your rate." actions={<AddService onAdded={load} />} />
      <div className="space-y-6">{list?.map((s) => <ServiceCard key={s.id} s={s} onChanged={load} />)}</div>
    </div>
  );
}
